
import { supabase } from "@/integrations/supabase/client";

export type LogLevel = 'error' | 'warning' | 'info' | 'debug';
export type LogSource = 'api' | 'database' | 'auth' | 'frontend' | 'system' | 'certificate';

/**
 * Logs a system event to the system_logs table
 */
export const logSystemEvent = async ({
  level,
  source,
  message,
  details = null,
  component = null,
}: {
  level: LogLevel;
  source: LogSource;
  message: string;
  details?: string | null;
  component?: string | null;
}) => {
  try {
    // Check if user is authenticated before attempting to write logs
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (sessionData?.session) {
      const { error } = await supabase.from('system_logs').insert({
        level,
        source,
        message,
        details,
        component,
        status: 'unresolved',
      });

      if (error) {
        // Log to console but don't throw error to prevent disrupting app functionality
        console.warn('Error logging system event:', error);
      }
    } else {
      // User is not authenticated, just log to console instead
      console.info(`[LOG - ${source.toUpperCase()}] ${message}`, details ? { details } : '');
    }

    // Always log to console for development visibility
    const consoleMethod = level === 'error' ? console.error 
      : level === 'warning' ? console.warn 
      : level === 'debug' ? console.debug 
      : console.info;
      
    consoleMethod(`[${source.toUpperCase()}] ${message}`, details ? { details } : '');
    
  } catch (err) {
    // Log to console but don't throw error to prevent disrupting app functionality
    console.warn('Failed to log system event:', err);
  }
};

/**
 * Creates a scoped logger for a specific component or module
 */
export const createLogger = (source: LogSource, component?: string) => {
  return {
    error: (message: string, details?: string) => 
      logSystemEvent({ level: 'error', source, message, details, component }),
    warning: (message: string, details?: string) => 
      logSystemEvent({ level: 'warning', source, message, details, component }),
    info: (message: string, details?: string) => 
      logSystemEvent({ level: 'info', source, message, details, component }),
    debug: (message: string, details?: string) => 
      logSystemEvent({ level: 'debug', source, message, details, component }),
  };
};
